import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaMinus } from 'react-icons/fa';

// R2 - Service type definition
export interface Service {
  title: string;
  description: string | string[];
  icon: React.ReactNode;
}

// R2 - Animation variants for framer-motion
const cardVariants = {
  initial: { opacity: 0, y: 20 },
  animate: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: index * 0.1, duration: 0.4 },
  }),
};

const contentVariants = {
  hidden: { opacity: 0, height: 0, marginTop: 0 },
  visible: { 
    opacity: 1, 
    height: 'auto', 
    marginTop: 16,
    transition: { duration: 0.3, ease: [0.0, 0.0, 0.2, 1] as const },
  },
};

// R2 - Description content renderer (extracted to reduce complexity)
function DescriptionContent({ description }: { description: string | string[] }) {
  if (Array.isArray(description)) {
    return (
      <ul className="space-y-3 text-gray-700 leading-relaxed text-lg">
        {description.map((item, idx) => (
          <motion.li
            key={idx}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.1 + 0.2 }}
            className="flex items-baseline gap-3"
          >
            <span className="text-inboca-teal text-lg flex-shrink-0">•</span>
            <span>{item}</span>
          </motion.li>
        ))}
      </ul>
    );
  }

  return (
    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.15 }}
      className="text-gray-700 leading-relaxed text-lg"
    >
      {description}
    </motion.p>
  );
}

// R2 - Props interface
interface ServiceCardProps {
  service: Service;
  index: number;
  isActive: boolean;
  onToggle: () => void;
}

/**
 * R2 - Interactive service card with hover/tap behavior
 * R4 - Accessible with keyboard support and ARIA attributes
 */
export function ServiceCard({ service, index, isActive, onToggle }: ServiceCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // R2 - Detect touch device to separate hover/tap behavior
  const handleTouchStart = () => setIsTouchDevice(true);
  
  // On touch devices: only use isActive (tap toggle)
  // On desktop: use hover OR active state
  const showContent = isTouchDevice ? isActive : (isActive || isHovered);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <motion.div
      custom={index}
      variants={cardVariants}
      initial="initial"
      whileInView="animate"
      viewport={{ once: true }}
      className="relative"
    >
      <div
        role="button"
        tabIndex={0}
        aria-expanded={showContent}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        onTouchStart={handleTouchStart}
        onMouseEnter={() => !isTouchDevice && setIsHovered(true)}
        onMouseLeave={() => !isTouchDevice && setIsHovered(false)}
        className={`
          bg-white rounded-xl p-6 sm:p-8 shadow-md border cursor-pointer
          transition-all duration-300 ease-out h-full outline-none overflow-hidden
          focus-visible:ring-2 focus-visible:ring-inboca-teal focus-visible:ring-offset-2
          ${showContent 
            ? 'border-inboca-teal shadow-lg scale-[1.02]' 
            : 'border-gray-100 hover:border-inboca-teal/50'
          }
        `}
      >
        {/* Card Header - Always Visible */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className={`
              flex-shrink-0 flex items-center justify-center h-12 w-12 sm:h-14 sm:w-14 rounded-lg 
              transition-colors duration-300
              ${showContent 
                ? 'bg-gradient-to-br from-inboca-teal to-inboca-teal/80 text-white' 
                : 'bg-gradient-to-br from-inboca-teal/10 to-inboca-orange/10 text-inboca-teal'
              }
            `}>
              {service.icon}
            </div>
            <h3 className="text-lg sm:text-2xl font-bold text-gray-900 break-words hyphens-auto">
              {service.title}
            </h3>
          </div>
          
          {/* Expand/Collapse Indicator */}
          <motion.div
            animate={{ rotate: showContent ? 180 : 0 }}
            transition={{ duration: 0.3 }}
            className={`
              flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full
              transition-colors duration-300
              ${showContent 
                ? 'bg-inboca-teal text-white' 
                : 'bg-gray-100 text-gray-500'
              }
            `}
          >
            {showContent ? <FaMinus className="h-3 w-3" /> : <FaPlus className="h-3 w-3" />}
          </motion.div>
        </div>

        {/* Mobile Hint - Only visible on touch devices when not active */}
        <p className={`
          text-xs text-gray-400 mt-2 md:hidden transition-opacity duration-200
          ${isActive ? 'opacity-0' : 'opacity-100'}
        `}>
          Toca para ver más
        </p>

        {/* Expandable Content */}
        <AnimatePresence>
          {showContent && (
            <motion.div
              variants={contentVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="overflow-hidden"
            >
              <DescriptionContent description={service.description} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
